import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Course } from '../types/course';
import { courses } from '../data/courses';
import CourseCard from '../components/courses/CourseCard';
import LoginPromptModal from '../components/courses/LoginPromptModal';
import CourseDetailsModal from '../components/courses/CourseDetailsModal';
import { getEnrolledCourses } from '../lib/tokenStore';

export default function CoursesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<string[]>([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [displayedCourses, setDisplayedCourses] = useState<Course[]>([]);
  const [courseDetails, setCourseDetails] = useState<Course | null>(null);

  useEffect(() => {
    const initializePage = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (userId) {
          const enrolled = await getEnrolledCourses(parseInt(userId));
          setEnrolledCourseIds(enrolled);
          
          const updatedCourses = courses.map(course => ({
            ...course,
            enrolled: enrolled.includes(course.id),
            progress: 0
          }));
          
          setDisplayedCourses(updatedCourses);
        } else {
          setDisplayedCourses(courses);
        }
      } catch (error) {
        console.error('Failed to initialize page:', error);
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, []);

  const handleEnrollClick = (courseId: string) => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      setShowLoginPrompt(true);
      return;
    }
    navigate('/pricing');
  };

  const handleViewDetails = (course: Course) => {
    setCourseDetails(course);
  };

  const handleLoginPromptClose = () => {
    setShowLoginPrompt(false);
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 pt-24 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-24">
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Available Courses</h1>
          <p className="text-gray-300">
            Explore our collection of tech courses and start learning today.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedCourses.map(course => (
            <CourseCard
              key={course.id}
              course={course}
              onEnroll={handleEnrollClick}
              onViewDetails={handleViewDetails}
              disabled={course.enrolled}
            />
          ))}
        </div>

        {courseDetails && (
          <CourseDetailsModal
            course={courseDetails}
            onClose={() => setCourseDetails(null)}
          />
        )}

        <LoginPromptModal
          isOpen={showLoginPrompt}
          onClose={handleLoginPromptClose}
          onLogin={handleLoginRedirect}
        />
      </div>
    </div>
  );
}